// "Atiende" — the seat that owns/handles a client. Most agents split their book
// among their seats; this chip (next to Active/Inactive) shows who handles the
// client and drives ticket auto-assignment + the clients filter (Lissette
// 2026-06-23). The display name + color per seat are agency-wide
// (AgencySetting.seatLabels: { [userId]: { name, color } }); the assignment
// itself lives on Client.attendedBySeatId.

import { useState } from 'react';
import { Modal } from './ui.jsx';
import { verMenuEntero } from './CajaConScroll.jsx';
import {
  COLOR_ASIENTO, etiquetaDeAtiende, seatLabelFor, attendOptions,
} from '../atiende.js';

const DEFAULT_COLOR = COLOR_ASIENTO;

// seatLabelFor y attendOptions se mudaron a crm-app/atiende.js (misma logica)
// para que el importador de Excel pueda usarlas sin arrastrar React. Se siguen
// exportando desde aqui: deskte-full.jsx y el asistente de monday las importan
// de este archivo desde hace meses.
export { seatLabelFor, attendOptions };

// The compact chip + picker shown next to the status badge.
export function AttendedChip({ value, nombreLibre = '', seatLabels = {}, users = [], onChange, onEditLabels, lang, canEditLabels = false }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  // `nombreLibre` es el nombre que trajo una importacion cuando esa persona
  // todavia no tiene asiento (Lissette 2026-09-03). Se ensena igual, en gris y
  // con el borde punteado, para que se vea que aun no es un asiento de verdad.
  // Elegir uno en esta misma lista lo sustituye.
  const lbl = etiquetaDeAtiende(value, nombreLibre, seatLabels, users);
  const opts = attendOptions(users);
  const pick = (id) => { setOpen(false); if (onChange) onChange(id); };
  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button" onClick={() => setOpen((o) => !o)}
        title={lbl && lbl.libre
          ? (es ? `Atiende: ${lbl.name} — vino de una importacion y todavia no tiene asiento. Elige uno para asignarlo de verdad.` : `Handled by ${lbl.name} — imported name, no seat yet. Pick one to assign it for real.`)
          : (es ? 'Atiende (seat dueño del cliente)' : 'Handled by (owning seat)')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
          background: lbl ? `${lbl.color}22` : 'rgba(255,255,255,.04)',
          border: `1px ${lbl && lbl.libre ? 'dashed' : 'solid'} ${lbl ? lbl.color : 'var(--c-border,#1a3550)'}`,
          color: lbl ? lbl.color : 'var(--c-text3,#45688a)',
          padding: '.12rem .55rem', borderRadius: 100, fontSize: '.62rem', fontWeight: 700, fontFamily: 'var(--font-mono)',
        }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: lbl ? lbl.color : 'var(--c-text3,#45688a)', flexShrink: 0 }} />
        {es ? 'Atiende: ' : 'Handled: '}{lbl ? lbl.name : (es ? '—' : '—')}
        <span style={{ fontSize: '.55rem', opacity: 0.8 }}>▾</span>
      </button>
      {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />}
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 5px)', left: 0, zIndex: 40, background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.5)', padding: '.3rem', minWidth: 190, maxHeight: 320, overflowY: 'auto' }}>
          <button type="button" onClick={() => pick('')} style={optStyle(!value)}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--c-text3,#45688a)', flexShrink: 0 }} />
            {es ? 'Sin asignar' : 'Unassigned'}
          </button>
          {opts.map((u) => {
            const ul = seatLabelFor(u.id, seatLabels, users);
            return (
              <button key={u.id} type="button" onClick={() => pick(u.id)} style={optStyle(value === u.id)}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: ul.color, flexShrink: 0 }} />
                {ul.name}
                {value === u.id && <span style={{ marginLeft: 'auto', color: ul.color, fontSize: '.7rem' }}>✓</span>}
              </button>
            );
          })}
          {canEditLabels && onEditLabels && (
            <button type="button" onClick={() => { setOpen(false); onEditLabels(); }} style={{ ...optStyle(false), color: 'var(--c-cyan,#00d4ff)', borderTop: '1px solid var(--c-border,#1a3550)', marginTop: 2 }}>
              ⚙️ {es ? 'Editar etiquetas' : 'Edit labels'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Compact "Encargado" (assignee) picker for table cells — a dropdown of the
// team's seats + agents (Lissette 2026-07-11). Same seat labels/colors as
// AttendedChip, but fills its cell and shows just the name. The caller decides
// where the chosen userId is persisted (onboarding meta, policy customData…).
export function EncargadoPicker({ value, seatLabels = {}, users = [], onChange, lang, disabled }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const lbl = seatLabelFor(value, seatLabels, users);
  const opts = attendOptions(users);
  const pick = (id) => { setOpen(false); if (onChange) onChange(id); };
  return (
    <div style={{ position: 'relative', display: 'inline-flex', width: '100%' }}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} title={es ? 'Encargado' : 'Assignee'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, cursor: disabled ? 'default' : 'pointer', width: '100%', justifyContent: 'space-between',
          background: lbl ? `${lbl.color}18` : 'rgba(255,255,255,.03)',
          border: `1px solid ${lbl ? lbl.color + '55' : 'var(--c-border,#1a3550)'}`,
          color: lbl ? lbl.color : 'var(--c-text3,#45688a)',
          padding: '.25rem .5rem', borderRadius: 8, fontSize: '.7rem', fontWeight: 700, fontFamily: 'inherit',
        }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: lbl ? lbl.color : 'var(--c-text3,#45688a)', flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lbl ? lbl.name : (es ? 'Sin asignar' : 'Unassigned')}</span>
        </span>
        <span style={{ fontSize: '.55rem', opacity: 0.8 }}>▾</span>
      </button>
      {open && <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 39 }} />}
      {open && (
        <div ref={verMenuEntero} style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40, background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.5)', padding: '.3rem', minWidth: 180, maxHeight: 300, overflowY: 'auto' }}>
          <button type="button" onClick={() => pick('')} style={optStyle(!value)}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--c-text3,#45688a)', flexShrink: 0 }} />
            {es ? 'Sin asignar' : 'Unassigned'}
          </button>
          {opts.map((u) => {
            const ul = seatLabelFor(u.id, seatLabels, users);
            return (
              <button key={u.id} type="button" onClick={() => pick(u.id)} style={optStyle(value === u.id)}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: ul.color, flexShrink: 0 }} />
                {ul.name}
                {value === u.id && <span style={{ marginLeft: 'auto', color: ul.color, fontSize: '.7rem' }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const optStyle = (on) => ({
  display: 'flex', alignItems: 'center', gap: 7, width: '100%',
  background: on ? 'var(--c-surface2,#112538)' : 'none', border: 'none', borderRadius: 7,
  padding: '.4rem .55rem', cursor: 'pointer', fontFamily: 'inherit', fontSize: '.78rem',
  fontWeight: 600, color: 'var(--c-white,#eef6ff)', textAlign: 'left',
});

// Modal to set the custom name + color per seat (agency-wide).
export function SeatLabelsEditor({ seatLabels = {}, users = [], onChange, onClose, lang }) {
  const es = lang === 'es';
  const [draft, setDraft] = useState(() => ({ ...seatLabels }));
  const opts = attendOptions(users);
  const set = (id, key, val) => setDraft((d) => ({ ...d, [id]: { ...(d[id] || {}), [key]: val } }));
  const save = () => { if (onChange) onChange(draft); onClose && onClose(); };
  return (
    <Modal title={es ? 'Etiquetas de "Atiende"' : '"Handled by" labels'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
        <div style={{ fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)' }}>
          {es ? 'Nombre y color para cada seat. Se usan en el chip del cliente y en el filtro.' : 'Name and color for each seat. Used on the client chip and filter.'}
        </div>
        {opts.length === 0 && <div style={{ fontSize: '.8rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'No hay seats en el equipo todavía.' : 'No seats on the team yet.'}</div>}
        {opts.map((u) => {
          const d = draft[u.id] || {};
          return (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
              <input type="color" value={d.color || DEFAULT_COLOR} onChange={(e) => set(u.id, 'color', e.target.value)} style={{ width: 34, height: 34, border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, background: 'none', cursor: 'pointer', flexShrink: 0 }} />
              <input className="dkt-inp" style={{ flex: 1 }} placeholder={u.name || u.displayName || u.id} value={d.name || ''} onChange={(e) => set(u.id, 'name', e.target.value)} />
              <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', minWidth: 90, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name || u.displayName}</span>
            </div>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '.3rem' }}>
          <button type="button" className="dkt-btn ghost" onClick={onClose}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" className="dkt-btn primary" onClick={save}>{es ? 'Guardar' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}
